import React, { useMemo, useRef } from "react";
import axios from "axios";
import { NodeProps, useReactFlow } from "reactflow";
import { Main, Styled } from "./style";
import { webURL } from "../../../../../services/API";
import { AuthenticateSelect } from "../../../../auth/select";
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { updateNodeValue } from "../../../../../services/method";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";

type WhatsappListenerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

type ConnectionStatus =
  | "not_initialized"
  | "qr_pending"
  | "connecting"
  | "connected"
  | "disconnected"
  | "failed"
  | "requires_rescan";

type GroupsUiStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "error_disconnected"
  | "error_fetch"
  | "empty_groups";

type ListenMode = "all_messages" | "specific_groups_only" | "dm_only";

type GroupListItem = {
  jid: string;
  name: string;
  memberCount?: number;
  isStale?: boolean;
};

type GroupsSectionState = {
  status: GroupsUiStatus;
  items: GroupListItem[];
  selectedGroupJids: string[];
  listenMode: ListenMode;
  triggerStatus: boolean;
  triggerIncoming: boolean;
  triggerOutgoing: boolean;
  lastGroupSyncAt: string | null;
  connectionState?: string;
  errorMessage?: string;
  directionErrorMessage?: string;
  cooldownRemainingMs: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const extractString = (value: unknown): string =>
  typeof value === "string" ? value : "";

const normalizeListenMode = (value: unknown): ListenMode => {
  if (
    value === "all_messages" ||
    value === "specific_groups_only" ||
    value === "dm_only"
  ) {
    return value;
  }
  return "all_messages";
};

const normalizeTriggerIncoming = (value: unknown): boolean =>
  typeof value === "boolean" ? value : true;

const normalizeTriggerOutgoing = (value: unknown): boolean =>
  typeof value === "boolean" ? value : false;

const normalizeGroupJids = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return Array.from(
    new Set(
      value
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean),
    ),
  );
};

const isConnectionStatus = (value: unknown): value is ConnectionStatus =>
  value === "not_initialized" ||
  value === "qr_pending" ||
  value === "connecting" ||
  value === "connected" ||
  value === "disconnected" ||
  value === "failed" ||
  value === "requires_rescan";

const parseConnectionStatus = (value: unknown): ConnectionStatus =>
  isConnectionStatus(value) ? value : "not_initialized";

const extractQrImage = (payload: unknown): string => {
  if (!isRecord(payload)) {
    return "";
  }
  const candidates = [payload.qrDataUrl, payload.qrCodeDataUrl, payload.qrCode];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate;
    }
  }
  return "";
};

const getConnectionId = (value: unknown): string | null => {
  if (!isRecord(value)) {
    return null;
  }
  if (typeof value._id === "string" && value._id.trim()) {
    return value._id;
  }
  if (typeof value.id === "string" && value.id.trim()) {
    return value.id;
  }
  return null;
};

const arraysEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const getDisconnectedStateMessage = (state?: string): string => {
  switch (state) {
    case "qr_pending":
      return "Scan QR code to connect WhatsApp, then refresh groups.";
    case "connecting":
      return "WhatsApp is connecting. Try refresh in a few seconds.";
    case "disconnected":
      return "WhatsApp disconnected. Reconnect the account first.";
    case "requires_rescan":
      return "Session expired. Re-scan QR, then refresh.";
    case "failed":
      return "Connection failed. Reinitialize WhatsApp and refresh.";
    case "not_initialized":
    default:
      return "Initialize WhatsApp connection first.";
  }
};

const parseGroupList = (value: unknown): GroupListItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is Record<string, unknown> => isRecord(item))
    .map((item) => ({
      jid: extractString(item.jid),
      name: extractString(item.name) || extractString(item.jid),
      ...(typeof item.memberCount === "number" ? { memberCount: item.memberCount } : {}),
      ...(typeof item.isStale === "boolean" ? { isStale: item.isStale } : {}),
    }))
    .filter((item) => item.jid.length > 0)
    .slice(0, 50);
};

const formatLastSynced = (value: string | null, nowMs: number): string => {
  if (!value) {
    return "Last synced: never";
  }
  const syncedAt = new Date(value).getTime();
  if (!Number.isFinite(syncedAt)) {
    return "Last synced: unknown";
  }
  const diffMs = Math.max(0, nowMs - syncedAt);
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin <= 0) {
    return "Last synced just now";
  }
  if (diffMin === 1) {
    return "Last synced 1 minute ago";
  }
  return `Last synced ${diffMin} minutes ago`;
};

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const serverMessage =
      isRecord(error.response?.data) && typeof error.response?.data?.message === "string"
        ? error.response?.data?.message
        : "";
    return serverMessage || error.message || "Request failed";
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Request failed";
};

export function WhatsappListener({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: WhatsappListenerProps) {
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const wasExpanded = useRef(false);
  const [connectionStatus, setConnectionStatus] =
    React.useState<ConnectionStatus>("not_initialized");
  const [qrCode, setQrCode] = React.useState<string>("");
  const [phoneNumber, setPhoneNumber] = React.useState<string>("");
  const [timeNow, setTimeNow] = React.useState<number>(Date.now());
  const [testPhone, setTestPhone] = React.useState<string>("");
  const [testMessage, setTestMessage] = React.useState<string>("");
  const [testMessageSetAt, setTestMessageSetAt] = React.useState<number | null>(null);
  const statusCheckInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const registerDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const registerAbortRef = useRef<AbortController | null>(null);
  const groupsFetchAbortRef = useRef<AbortController | null>(null);

  const projectId = useMemo(
    () => new URLSearchParams(window.location.search).get("id") || "",
    [],
  );

  const initialListenMode = normalizeListenMode(data.value?.listenMode);
  const initialSelectedJids = normalizeGroupJids(data.value?.selectedGroupJids);
  const initialTriggerStatus =
    typeof data.value?.triggerStatus === "boolean" ? data.value.triggerStatus : false;
  const initialTriggerIncoming = normalizeTriggerIncoming(data.value?.triggerIncoming);
  const initialTriggerOutgoing = normalizeTriggerOutgoing(data.value?.triggerOutgoing);
  const initialLastSync =
    typeof data.value?.lastGroupSyncAt === "string" ? data.value.lastGroupSyncAt : null;

  const [groupsSection, setGroupsSection] = React.useState<GroupsSectionState>({
    status: "idle",
    items: [],
    selectedGroupJids: initialSelectedJids,
    listenMode: initialListenMode,
    triggerStatus: initialTriggerStatus,
    triggerIncoming: initialTriggerIncoming,
    triggerOutgoing: initialTriggerOutgoing,
    lastGroupSyncAt: initialLastSync,
    cooldownRemainingMs: 0,
  });

  const storedConnectionId =
    extractString(data.value?.connectionId) ||
    extractString(data.value?.connection?._id) ||
    extractString(data.value?.connection?.id) ||
    null;
  const hasConnection = Boolean(storedConnectionId);
  const isConnected = connectionStatus === "connected";
  const isWarning = !hasConnection || connectionStatus === "failed";
  const lastSyncedText = useMemo(
    () => formatLastSynced(groupsSection.lastGroupSyncAt, timeNow),
    [groupsSection.lastGroupSyncAt, timeNow],
  );
  const refreshSeconds = Math.ceil(groupsSection.cooldownRemainingMs / 1000);

  const persistListenerConfig = React.useCallback(
    (
      listenMode: ListenMode,
      selectedGroupJids: string[],
      triggerStatus: boolean,
      triggerIncoming: boolean,
      triggerOutgoing: boolean,
    ) => {
      updateNodeValue(id, setNodes, {
        listenMode,
        selectedGroupJids,
        triggerStatus,
        triggerIncoming,
        triggerOutgoing,
      });
    },
    [id, setNodes],
  );

  const scheduleRegister = React.useCallback(
    (
      connectionId: string,
      listenMode: ListenMode,
      selectedGroupJids: string[],
      triggerStatus: boolean,
      triggerIncoming: boolean,
      triggerOutgoing: boolean,
    ) => {
      if (!projectId) {
        return;
      }

      if (registerDebounceRef.current) {
        clearTimeout(registerDebounceRef.current);
      }
      if (registerAbortRef.current) {
        registerAbortRef.current.abort();
      }

      registerDebounceRef.current = setTimeout(async () => {
        const controller = new AbortController();
        registerAbortRef.current = controller;

        try {
          await axios.post(
            `${webURL}/triggers/whatsapp/register-listener`,
            {
              connectionId,
              projectId,
              nodeId: id,
              nodeIndex: data.index,
              listenMode,
              selectedGroupJids,
              triggerStatus,
              triggerIncoming,
              triggerOutgoing,
            },
            { signal: controller.signal },
          );
          setGroupsSection((prev) => ({ ...prev, errorMessage: undefined }));
        } catch (error: unknown) {
          if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
            return;
          }
          const message = getErrorMessage(error);
          setGroupsSection((prev) => ({ ...prev, errorMessage: message }));
          console.error("[WhatsApp] Register listener error:", message);
        } finally {
          if (registerAbortRef.current === controller) {
            registerAbortRef.current = null;
          }
        }
      }, 300);
    },
    [data.index, id, projectId],
  );

  const fetchGroups = React.useCallback(
    async (connectionId: string, forceRefresh: boolean) => {
      if (groupsFetchAbortRef.current) {
        groupsFetchAbortRef.current.abort();
      }
      const controller = new AbortController();
      groupsFetchAbortRef.current = controller;

      setGroupsSection((prev) => ({
        ...prev,
        status: "loading",
        errorMessage: undefined,
      }));

      try {
        const response = await axios.get(`${webURL}/triggers/whatsapp/groups`, {
          params: {
            connectionId,
            ...(forceRefresh ? { forceRefresh: true } : {}),
          },
          signal: controller.signal,
        });
        const payload = isRecord(response.data) ? response.data : {};
        const groups = parseGroupList(payload.groups);
        const cooldownRemainingMs =
          typeof payload.cooldownRemainingMs === "number" ? payload.cooldownRemainingMs : 0;
        const lastGroupSyncAt =
          typeof payload.lastGroupSyncAt === "string" ? payload.lastGroupSyncAt : null;

        setGroupsSection((prev) => ({
          ...prev,
          status: groups.length === 0 ? "empty_groups" : "loaded",
          items: groups,
          cooldownRemainingMs: Math.max(0, cooldownRemainingMs),
          lastGroupSyncAt,
          connectionState: undefined,
          errorMessage: undefined,
        }));
        updateNodeValue(id, setNodes, { lastGroupSyncAt });
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
          return;
        }

        if (
          axios.isAxiosError(error) &&
          error.response?.status === 503 &&
          isRecord(error.response?.data) &&
          error.response?.data?.error === "WHATSAPP_NOT_CONNECTED"
        ) {
          const state = extractString(error.response?.data?.state);
          setGroupsSection((prev) => ({
            ...prev,
            status: "error_disconnected",
            connectionState: state,
            errorMessage: getDisconnectedStateMessage(state),
          }));
          return;
        }

        setGroupsSection((prev) => ({
          ...prev,
          status: "error_fetch",
          errorMessage: getErrorMessage(error),
        }));
      } finally {
        if (groupsFetchAbortRef.current === controller) {
          groupsFetchAbortRef.current = null;
        }
      }
    },
    [id, setNodes],
  );

  const startStatusChecking = React.useCallback(
    (connectionId: string) => {
      if (statusCheckInterval.current) {
        clearInterval(statusCheckInterval.current);
      }

      statusCheckInterval.current = setInterval(async () => {
        try {
          const response = await axios.get(
            `${webURL}/triggers/whatsapp/status?connectionId=${connectionId}`,
          );
          const nextStatus = parseConnectionStatus(response.data?.status);
          setConnectionStatus(nextStatus);

          if (nextStatus === "connected") {
            setPhoneNumber(extractString(response.data?.phoneNumber));
            setQrCode("");
            if (statusCheckInterval.current) {
              clearInterval(statusCheckInterval.current);
            }
            scheduleRegister(
              connectionId,
              groupsSection.listenMode,
              groupsSection.selectedGroupJids,
              groupsSection.triggerStatus,
              groupsSection.triggerIncoming,
              groupsSection.triggerOutgoing,
            );
          } else if (nextStatus === "qr_pending") {
            setQrCode(extractQrImage(response.data));
          }
        } catch (error) {
          console.error("[WhatsApp] Status check error:", getErrorMessage(error));
        }
      }, 3000);
    },
    [
      groupsSection.listenMode,
      groupsSection.selectedGroupJids,
      groupsSection.triggerStatus,
      groupsSection.triggerIncoming,
      groupsSection.triggerOutgoing,
      scheduleRegister,
    ],
  );

  const initializeConnection = React.useCallback(
    async (connectionId: string) => {
      try {
        const response = await axios.post(`${webURL}/triggers/whatsapp/initialize`, {
          connectionId,
        });
        const status = parseConnectionStatus(response.data?.status);
        setConnectionStatus(status);

        if (status === "qr_pending") {
          setQrCode(extractQrImage(response.data));
          scheduleRegister(
            connectionId,
            groupsSection.listenMode,
            groupsSection.selectedGroupJids,
            groupsSection.triggerStatus,
            groupsSection.triggerIncoming,
            groupsSection.triggerOutgoing,
          );
          startStatusChecking(connectionId);
          return;
        }

        if (status === "connected") {
          setPhoneNumber(extractString(response.data?.phoneNumber));
          scheduleRegister(
            connectionId,
            groupsSection.listenMode,
            groupsSection.selectedGroupJids,
            groupsSection.triggerStatus,
            groupsSection.triggerIncoming,
            groupsSection.triggerOutgoing,
          );
        }
      } catch (error) {
        console.error("[WhatsApp] Initialize error:", getErrorMessage(error));
        setConnectionStatus("failed");
      }
    },
    [
      groupsSection.listenMode,
      groupsSection.selectedGroupJids,
      groupsSection.triggerStatus,
      groupsSection.triggerIncoming,
      groupsSection.triggerOutgoing,
      scheduleRegister,
      startStatusChecking,
    ],
  );

  const checkConnectionStatus = React.useCallback(
    async (connectionId: string) => {
      try {
        const response = await axios.get(
          `${webURL}/triggers/whatsapp/status?connectionId=${connectionId}`,
        );
        const status = parseConnectionStatus(response.data?.status);
        setConnectionStatus(status);

        if (status === "connected") {
          setPhoneNumber(extractString(response.data?.phoneNumber));
          scheduleRegister(
            connectionId,
            groupsSection.listenMode,
            groupsSection.selectedGroupJids,
            groupsSection.triggerStatus,
            groupsSection.triggerIncoming,
            groupsSection.triggerOutgoing,
          );
          return;
        }

        if (status === "qr_pending") {
          setQrCode(extractQrImage(response.data));
          scheduleRegister(
            connectionId,
            groupsSection.listenMode,
            groupsSection.selectedGroupJids,
            groupsSection.triggerStatus,
            groupsSection.triggerIncoming,
            groupsSection.triggerOutgoing,
          );
          startStatusChecking(connectionId);
          return;
        }

        if (
          status === "not_initialized" ||
          status === "disconnected" ||
          status === "failed" ||
          status === "requires_rescan"
        ) {
          await initializeConnection(connectionId);
        }
      } catch (error) {
        console.error("[WhatsApp] Status check error:", getErrorMessage(error));
        await initializeConnection(connectionId);
      }
    },
    [
      groupsSection.listenMode,
      groupsSection.selectedGroupJids,
      groupsSection.triggerStatus,
      groupsSection.triggerIncoming,
      groupsSection.triggerOutgoing,
      initializeConnection,
      scheduleRegister,
      startStatusChecking,
    ],
  );

  const handleConnectionChange = React.useCallback(
    async (connection: unknown) => {
      const connectionId = getConnectionId(connection);
      if (!connectionId) {
        return;
      }

      updateNodeValue(id, setNodes, { connection, connectionId });
      await checkConnectionStatus(connectionId);
    },
    [checkConnectionStatus, id, setNodes],
  );

  React.useEffect(() => {
    if (setChildMinimize) {
      setChildMinimize(() => setIsExpanded((prev) => !prev));
    }
    if (setChildSettings) {
      setChildSettings(() => setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle })));
    }
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    const dataMode = normalizeListenMode(data.value?.listenMode);
    const dataSelected = normalizeGroupJids(data.value?.selectedGroupJids);
    const dataTriggerStatus =
      typeof data.value?.triggerStatus === "boolean" ? data.value.triggerStatus : false;
    const dataTriggerIncoming = normalizeTriggerIncoming(data.value?.triggerIncoming);
    const dataTriggerOutgoing = normalizeTriggerOutgoing(data.value?.triggerOutgoing);
    setGroupsSection((prev) => {
      if (
        prev.listenMode === dataMode &&
        arraysEqual(prev.selectedGroupJids, dataSelected) &&
        prev.triggerStatus === dataTriggerStatus &&
        prev.triggerIncoming === dataTriggerIncoming &&
        prev.triggerOutgoing === dataTriggerOutgoing
      ) {
        return prev;
      }
      return {
        ...prev,
        listenMode: dataMode,
        selectedGroupJids: dataSelected,
        triggerStatus: dataTriggerStatus,
        triggerIncoming: dataTriggerIncoming,
        triggerOutgoing: dataTriggerOutgoing,
        directionErrorMessage: undefined,
      };
    });
  }, [
    data.value?.listenMode,
    data.value?.selectedGroupJids,
    data.value?.triggerStatus,
    data.value?.triggerIncoming,
    data.value?.triggerOutgoing,
  ]);

  React.useEffect(() => {
    if (!storedConnectionId) {
      return;
    }
    void checkConnectionStatus(storedConnectionId);
  }, [checkConnectionStatus, storedConnectionId]);

  React.useEffect(() => {
    if (isExpanded && !wasExpanded.current && data.value?.connection) {
      wasExpanded.current = true;
      void handleConnectionChange(data.value.connection);
    }
    if (!isExpanded) {
      wasExpanded.current = false;
    }
  }, [data.value?.connection, handleConnectionChange, isExpanded]);

  React.useEffect(() => {
    if (
      !isExpanded ||
      !storedConnectionId ||
      connectionStatus !== "connected" ||
      groupsSection.listenMode !== "specific_groups_only" ||
      groupsSection.status !== "idle"
    ) {
      return;
    }
    void fetchGroups(storedConnectionId, false);
  }, [
    connectionStatus,
    fetchGroups,
    groupsSection.listenMode,
    groupsSection.status,
    isExpanded,
    storedConnectionId,
  ]);

  React.useEffect(() => {
    const interval = setInterval(() => setTimeNow(Date.now()), 30_000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    if (groupsSection.cooldownRemainingMs <= 0) {
      return;
    }
    const interval = setInterval(() => {
      setGroupsSection((prev) => ({
        ...prev,
        cooldownRemainingMs: Math.max(0, prev.cooldownRemainingMs - 1_000),
      }));
    }, 1_000);
    return () => clearInterval(interval);
  }, [groupsSection.cooldownRemainingMs]);

  React.useEffect(() => {
    return () => {
      if (statusCheckInterval.current) {
        clearInterval(statusCheckInterval.current);
      }
      if (registerDebounceRef.current) {
        clearTimeout(registerDebounceRef.current);
      }
      if (registerAbortRef.current) {
        registerAbortRef.current.abort();
      }
      if (groupsFetchAbortRef.current) {
        groupsFetchAbortRef.current.abort();
      }
    };
  }, []);

  const handleModeChange = (nextMode: ListenMode) => {
    const selected = groupsSection.selectedGroupJids;
    const triggerStatus = groupsSection.triggerStatus;
    const triggerIncoming = groupsSection.triggerIncoming;
    const triggerOutgoing = groupsSection.triggerOutgoing;
    setGroupsSection((prev) => ({
      ...prev,
      listenMode: nextMode,
      errorMessage: undefined,
      directionErrorMessage: undefined,
    }));
    persistListenerConfig(
      nextMode,
      selected,
      triggerStatus,
      triggerIncoming,
      triggerOutgoing,
    );

    if (storedConnectionId) {
      scheduleRegister(
        storedConnectionId,
        nextMode,
        selected,
        triggerStatus,
        triggerIncoming,
        triggerOutgoing,
      );
    }

    if (
      nextMode === "specific_groups_only" &&
      storedConnectionId &&
      connectionStatus === "connected" &&
      groupsSection.status === "idle"
    ) {
      void fetchGroups(storedConnectionId, false);
    }
  };

  const handleToggleGroup = (jid: string) => {
    const normalized = jid.trim().toLowerCase();
    const nextSelected = groupsSection.selectedGroupJids.includes(normalized)
      ? groupsSection.selectedGroupJids.filter((item) => item !== normalized)
      : [...groupsSection.selectedGroupJids, normalized];

    setGroupsSection((prev) => ({
      ...prev,
      selectedGroupJids: nextSelected,
      directionErrorMessage: undefined,
    }));
    persistListenerConfig(
      groupsSection.listenMode,
      nextSelected,
      groupsSection.triggerStatus,
      groupsSection.triggerIncoming,
      groupsSection.triggerOutgoing,
    );
    if (storedConnectionId) {
      scheduleRegister(
        storedConnectionId,
        groupsSection.listenMode,
        nextSelected,
        groupsSection.triggerStatus,
        groupsSection.triggerIncoming,
        groupsSection.triggerOutgoing,
      );
    }
  };

  const handleClearSelection = () => {
    setGroupsSection((prev) => ({
      ...prev,
      selectedGroupJids: [],
      directionErrorMessage: undefined,
    }));
    persistListenerConfig(
      groupsSection.listenMode,
      [],
      groupsSection.triggerStatus,
      groupsSection.triggerIncoming,
      groupsSection.triggerOutgoing,
    );
    if (storedConnectionId) {
      scheduleRegister(
        storedConnectionId,
        groupsSection.listenMode,
        [],
        groupsSection.triggerStatus,
        groupsSection.triggerIncoming,
        groupsSection.triggerOutgoing,
      );
    }
  };

  const handleTriggerStatusChange = (nextValue: boolean) => {
    setGroupsSection((prev) => ({
      ...prev,
      triggerStatus: nextValue,
      errorMessage: undefined,
      directionErrorMessage: undefined,
    }));
    persistListenerConfig(
      groupsSection.listenMode,
      groupsSection.selectedGroupJids,
      nextValue,
      groupsSection.triggerIncoming,
      groupsSection.triggerOutgoing,
    );
    if (storedConnectionId) {
      scheduleRegister(
        storedConnectionId,
        groupsSection.listenMode,
        groupsSection.selectedGroupJids,
        nextValue,
        groupsSection.triggerIncoming,
        groupsSection.triggerOutgoing,
      );
    }
  };

  const handleDirectionChange = (
    direction: "incoming" | "outgoing",
    checked: boolean,
  ) => {
    const nextTriggerIncoming =
      direction === "incoming" ? checked : groupsSection.triggerIncoming;
    const nextTriggerOutgoing =
      direction === "outgoing" ? checked : groupsSection.triggerOutgoing;

    if (!nextTriggerIncoming && !nextTriggerOutgoing) {
      setGroupsSection((prev) => ({
        ...prev,
        directionErrorMessage:
          "At least one direction must be enabled (incoming or outgoing).",
      }));
      return;
    }

    setGroupsSection((prev) => ({
      ...prev,
      triggerIncoming: nextTriggerIncoming,
      triggerOutgoing: nextTriggerOutgoing,
      errorMessage: undefined,
      directionErrorMessage: undefined,
    }));

    persistListenerConfig(
      groupsSection.listenMode,
      groupsSection.selectedGroupJids,
      groupsSection.triggerStatus,
      nextTriggerIncoming,
      nextTriggerOutgoing,
    );

    if (storedConnectionId) {
      scheduleRegister(
        storedConnectionId,
        groupsSection.listenMode,
        groupsSection.selectedGroupJids,
        groupsSection.triggerStatus,
        nextTriggerIncoming,
        nextTriggerOutgoing,
      );
    }
  };

  // Clicking "Run" on this node calls the same executeRun path a real message
  // does (handleWhatsAppRun's "listen" branch), but with no live webhook context
  // there's nothing to return an incoming message from — it falls back to
  // reading data.value.memory (a node-level array of fake messages) and returns
  // empty when that's unset, which it always is: nothing in the UI ever wrote to
  // it. This mirrors /whatsapp/dev-inject's fake-message shape exactly, but sets
  // it locally on the node instead of calling the backend, so a subsequent
  // "Run" click resolves phoneNumber/body/etc. for real, right here on canvas.
  const handleSetTestMessage = () => {
    const digitsPhone = testPhone.replace(/\D/g, "") || "201234567890";
    const fakeMessage = {
      phoneNumber: digitsPhone,
      body: testMessage || "تمام",
      fromName: "Test Customer",
      to: "",
      timestamp: Math.floor(Date.now() / 1000),
      isGroup: false,
      messageId: `fake-${Date.now()}`,
      chatId: `${digitsPhone}@s.whatsapp.net`,
      hasMedia: false,
    };
    updateNodeValue(id, setNodes, { memory: [fakeMessage] });
    setTestMessageSetAt(Date.now());
  };

  const renderGroupsContent = () => {
    if (groupsSection.status === "loading") {
      return <div className="groupsStatus">Loading groups...</div>;
    }

    if (groupsSection.status === "error_disconnected") {
      return <div className="groupsStatus error">{groupsSection.errorMessage}</div>;
    }

    if (groupsSection.status === "error_fetch") {
      return <div className="groupsStatus error">{groupsSection.errorMessage}</div>;
    }

    if (groupsSection.status === "empty_groups") {
      return <div className="groupsStatus">No WhatsApp groups found for this account.</div>;
    }

    if (groupsSection.status === "loaded") {
      return (
        <div className="groupsChecklist">
          {groupsSection.items.map((group) => (
            <label key={group.jid} className="groupItem">
              <input
                type="checkbox"
                checked={groupsSection.selectedGroupJids.includes(group.jid)}
                onChange={() => handleToggleGroup(group.jid)}
              />
              <span className="groupMeta">
                <span className="groupName">{group.name}</span>
                <span className="groupJid">{group.jid}</span>
                {typeof group.memberCount === "number" ? (
                  <span className="groupMembers">{group.memberCount} members</span>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      );
    }

    return <div className="groupsStatus">Choose "Specific groups only" to load groups.</div>;
  };

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : isConnected ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon">
              <IoCheckmarkCircleOutline size={21} />
            </span>
          </>
        ) : null}

        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: "420px" }}
          >
            <Styled>
              <div className="section">
                <div className="label">WhatsApp Connection</div>
                <AuthenticateSelect
                  type="whatsapp"
                  defaultId={storedConnectionId || ""}
                  defaultConnection={data.value?.connection}
                  handleAuthenticate={(type, connection) => {
                    if (type === "add") {
                      void handleConnectionChange(connection);
                    } else {
                      updateNodeValue(id, setNodes, {
                        connection: null,
                        connectionId: null,
                      });
                      setConnectionStatus("not_initialized");
                      setQrCode("");
                      setPhoneNumber("");
                      setGroupsSection((prev) => ({ ...prev, status: "idle", items: [] }));
                    }
                  }}
                  withLine={hasConnection}
                  background={data.background}
                />
              </div>

              {hasConnection ? (
                <>
                  <div className="section">
                    <div className="label">Connection Status</div>
                    <div className="status-container">
                      {connectionStatus === "qr_pending" ? (
                        <div className="qr-section">
                          <div className="status-text">Scan QR Code with WhatsApp</div>
                          {qrCode ? (
                            <div className="qr-code">
                              <img src={qrCode} alt="WhatsApp QR Code" />
                            </div>
                          ) : null}
                          <div className="helper-text">
                            1. Open WhatsApp on your phone
                            <br />
                            2. Open Linked Devices
                            <br />
                            3. Tap "Link a Device"
                            <br />
                            4. Scan this QR code
                          </div>
                        </div>
                      ) : null}

                      {connectionStatus === "connecting" ? (
                        <div className="status-text">Connecting...</div>
                      ) : null}

                      {connectionStatus === "connected" ? (
                        <div className="status-connected">
                          <div className="status-icon">✓</div>
                          <div>
                            <div className="status-text">Connected</div>
                            {phoneNumber ? (
                              <div className="phone-number">{phoneNumber}</div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}

                      {connectionStatus === "disconnected" ? (
                        <div className="status-text error">Disconnected</div>
                      ) : null}
                      {connectionStatus === "requires_rescan" ? (
                        <div className="status-text error">Requires re-scan</div>
                      ) : null}
                      {connectionStatus === "failed" ? (
                        <div className="status-text error">Connection failed</div>
                      ) : null}
                      {connectionStatus === "not_initialized" ? (
                        <div className="status-text">Not initialized</div>
                      ) : null}
                    </div>
                  </div>

                  <div className="section">
                    <div className="label">Listener Mode</div>
                    <div className="modeRow">
                      <label>
                        <input
                          type="radio"
                          checked={groupsSection.listenMode === "all_messages"}
                          onChange={() => handleModeChange("all_messages")}
                        />
                        <span>All messages</span>
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={groupsSection.listenMode === "specific_groups_only"}
                          onChange={() => handleModeChange("specific_groups_only")}
                        />
                        <span>Specific groups only</span>
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={groupsSection.listenMode === "dm_only"}
                          onChange={() => handleModeChange("dm_only")}
                        />
                        <span>DM only (not implemented)</span>
                      </label>
                    </div>
                  </div>

                  <div className="section">
                    <div className="label">Message Direction</div>
                    <div className="directionRow">
                      <label>
                        <input
                          type="checkbox"
                          checked={groupsSection.triggerIncoming}
                          onChange={(event) =>
                            handleDirectionChange("incoming", event.target.checked)
                          }
                        />
                        <span>Incoming messages</span>
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={groupsSection.triggerOutgoing}
                          onChange={(event) =>
                            handleDirectionChange("outgoing", event.target.checked)
                          }
                        />
                        <span>Outgoing messages</span>
                      </label>
                    </div>
                    <div className="helper-text">
                      Default: Incoming messages enabled, outgoing messages disabled.
                    </div>
                    {groupsSection.directionErrorMessage ? (
                      <div className="sectionError">{groupsSection.directionErrorMessage}</div>
                    ) : null}
                  </div>

                  <div className="section">
                    <div className="label">Status Messages</div>
                    <label className="toggleRow">
                      <input
                        type="checkbox"
                        checked={groupsSection.triggerStatus}
                        onChange={(event) =>
                          handleTriggerStatusChange(event.target.checked)
                        }
                      />
                      <span>Trigger WhatsApp Status</span>
                    </label>
                  </div>

                  {groupsSection.listenMode === "specific_groups_only" ? (
                    <div className="section">
                      <div className="label">Groups Filter</div>
                      <div className="groupsToolbar">
                        <button
                          className="smallBtn"
                          disabled={
                            connectionStatus !== "connected" ||
                            groupsSection.status === "loading" ||
                            refreshSeconds > 0
                          }
                          onClick={() => {
                            if (!storedConnectionId) {
                              return;
                            }
                            void fetchGroups(storedConnectionId, true);
                          }}
                        >
                          {refreshSeconds > 0
                            ? `Refresh available in ${refreshSeconds}s`
                            : "Refresh"}
                        </button>
                        <button
                          className="smallBtn clear"
                          onClick={handleClearSelection}
                          disabled={groupsSection.selectedGroupJids.length === 0}
                        >
                          Clear selection
                        </button>
                      </div>
                      <div className="syncMeta">{lastSyncedText}</div>
                      {renderGroupsContent()}
                    </div>
                  ) : null}

                  {groupsSection.errorMessage ? (
                    <div className="sectionError">{groupsSection.errorMessage}</div>
                  ) : null}

                  <div className="section">
                    <div className="label">Test Message (for the Run button)</div>
                    <div className="testMessageRow">
                      <input
                        type="text"
                        placeholder="Phone (e.g., 201005307391)"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                      />
                      <input
                        type="text"
                        placeholder="Message body (optional)"
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                      />
                      <button className="smallBtn" onClick={handleSetTestMessage} type="button">
                        Set Test Message
                      </button>
                    </div>
                    <div className="helper-text">
                      Clicking "Run" on this node has no real incoming message to read —
                      it shows empty output unless a test message is set here first.
                      This only affects the canvas/Run button; it does not send or
                      receive anything on WhatsApp.
                      {testMessageSetAt ? " Test message set — click Run now." : ""}
                    </div>
                  </div>

                  <div className="section">
                    <div className="label">Trigger Information</div>
                    <div className="info-box">
                      <p>
                        This trigger activates the workflow on incoming and/or
                        outgoing WhatsApp messages from the connected account,
                        based on the selected direction checkboxes.
                      </p>
                      <p>
                        Default direction is incoming only (incoming enabled,
                        outgoing disabled).
                      </p>
                      <p>
                        Exposed fields include: phoneNumber, body, fromName, to,
                        timestamp, isGroup, groupName, hasMedia, mediaType.
                      </p>
                    </div>
                  </div>
                </>
              ) : null}
            </Styled>
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}
